// The radio player (Brief 4 / P4a) — the eyes-free face, made AUDIBLE and BRAKED.
// On a session surface: voice the session's recent trace as a metered clip
// (POST /radio/clip DEBITS the session budget), play it, and show the live spend
// meter + remaining + an OFF switch. Cost is opt-in, shown, and stoppable —
// "show the plan, keep the brakes" extended to money: you always see what it
// costs and choose it, and the cap is the server's, never invented here.
//
// Safety: a fetched clip is cached client-side, so a Replay is FREE and an
// autoplay-block never re-bills (the button falls back to a direct-gesture
// Play). The 402 (cap reached) reads as ONE calm line, never an alarm. Token-
// pure; cost in text.cost, never the reserved attention hue.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  getRadioBudget,
  getTrace,
  postRadioClip,
} from "../api/client";
import type { RadioBudget, TraceEvent } from "@waytide/core";
import {
  composeSpoken,
  formatMicros,
  isLowBudget,
  meterFraction,
} from "./radioModel";
import "./radioControl.css";

/** Match the server policy's low-budget threshold (the calm heads-up point). */
const LOW_BUDGET_PCT = 80;

type PlayState = "idle" | "loading" | "playing" | "capped" | "error";

export function RadioControl({
  threadId,
  events,
  label,
}: {
  threadId: number;
  /** The session's trace, if it's already on screen. When omitted (the Radio
   * hub lists sessions without their traces), the control fetches it on Listen
   * so it can stand alone. */
  events?: TraceEvent[];
  label: string;
}) {
  const [budget, setBudget] = useState<RadioBudget | null>(null);
  const [state, setState] = useState<PlayState>("idle");
  const [clipSrc, setClipSrc] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let alive = true;
    getRadioBudget(threadId)
      .then((b) => alive && setBudget(b))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [threadId]);

  const play = useCallback((src: string) => {
    const a = audioRef.current;
    if (!a) return;
    a.src = src;
    a.onended = () => setState("idle");
    // play() may reject if autoplay is blocked; the clip is already cached +
    // paid, so fall back to idle (the button becomes a direct-gesture Play).
    a.play()
      .then(() => setState("playing"))
      .catch(() => setState("idle"));
  }, []);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
    setState("idle");
  }, []);

  const listen = useCallback(() => {
    // A cached clip replays for FREE — never re-bill for the same audio.
    if (clipSrc) {
      play(clipSrc);
      return;
    }
    setState("loading");
    // Use the trace already on screen, or fetch it (the Radio hub passes none).
    const resolveSpoken = events
      ? Promise.resolve(composeSpoken(events, label))
      : getTrace(String(threadId)).then((r) => composeSpoken(r.events, label));
    resolveSpoken
      .then((spoken) => postRadioClip(threadId, spoken))
      .then((clip) => {
        // Reconcile to the SERVER's post-clip remaining (the truth).
        setBudget((prev) =>
          prev
            ? {
                ...prev,
                remaining_micros: clip.remaining_micros,
                spent_micros: prev.cap_micros - clip.remaining_micros,
              }
            : prev,
        );
        const src = `data:${clip.content_type};base64,${clip.audio_base64}`;
        setClipSrc(src);
        play(src);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 402) {
          setState("capped");
          getRadioBudget(threadId)
            .then(setBudget)
            .catch(() => {});
        } else {
          setState("error");
        }
      });
  }, [clipSrc, events, label, threadId, play]);

  const cap = budget?.cap_micros ?? 0;
  const remaining = budget?.remaining_micros ?? 0;
  const fraction = budget ? meterFraction(cap, remaining) : 0;
  const low = budget ? isLowBudget(cap, remaining, LOW_BUDGET_PCT) : false;
  const capped = state === "capped" || (budget != null && remaining <= 0);

  const label_text =
    state === "loading"
      ? "Synthesizing…"
      : clipSrc
        ? "▷ Play"
        : "▷ Listen";

  return (
    <div className="wt-radio-ctl" data-testid="radio-control">
      <div className="wt-radio-ctl__row">
        {state === "playing" ? (
          <button
            type="button"
            className="wt-radio-ctl__btn"
            onClick={stop}
            data-testid="radio-off"
          >
            ■ Stop
          </button>
        ) : (
          <button
            type="button"
            className="wt-radio-ctl__btn"
            onClick={listen}
            disabled={state === "loading" || capped}
            data-testid="radio-listen"
          >
            {label_text}
          </button>
        )}

        {budget ? (
          <span className="wt-radio-ctl__meter" aria-label="Radio budget">
            <span className="wt-radio-ctl__bar">
              <span
                className="wt-radio-ctl__fill"
                style={{ width: `${Math.round(fraction * 100)}%` }}
              />
            </span>
            <span className="wt-radio-ctl__cost" data-testid="radio-remaining">
              {formatMicros(remaining)} left
            </span>
          </span>
        ) : null}
      </div>

      {capped ? (
        <p className="wt-radio-ctl__note">
          This session's radio budget is spent.
        </p>
      ) : low ? (
        <p className="wt-radio-ctl__note">Radio budget running low.</p>
      ) : state === "error" ? (
        <p className="wt-radio-ctl__note">Couldn't reach the radio just now.</p>
      ) : null}

      <audio ref={audioRef} hidden />
    </div>
  );
}
